// Look-ahead scheduling math (§4.11), kept pure so it can be unit-tested
// without an AudioContext.
//
// Timing model: playback is described by an ANCHOR — the AudioContext time at
// which a given played tick sounds, plus the speed factor and the loop. Every
// event time is derived from the anchor (`anchor.time + (sec(tick) −
// sec(anchor.tick)) / speed`, plus whole loop lengths), never accumulated, so
// there is no drift however long playback runs.
//
// A loop [A, B) splits playback into SEGMENTS: segment 0 runs from the anchor
// tick to B, every later segment runs A → B. Scheduling walks a CURSOR
// (segment, tick) forward through half-open tick ranges, so each event is
// scheduled exactly once even across loop wraps.

import { tickToSeconds, secondsToTick } from '@simplenotation/core';
import type { Timeline, NoteEvent, Track } from '@simplenotation/core';
import { PPQ } from '@simplenotation/theory';
import { harmonicMidi } from './sprite';

export interface LoopRange {
  startTick: number;
  endTick: number;
}

/** Tick ↔ song-seconds (speed 1) conversion for one timeline. */
export interface TimeMap {
  sec(tick: number): number;
  tick(sec: number): number;
}

export function timeMapOf(tl: Pick<Timeline, 'tempo'>): TimeMap {
  return {
    sec: (t) => tickToSeconds(tl, t, 1),
    tick: (s) => secondsToTick(tl, s, 1),
  };
}

export const MIN_SPEED = 0.25;
export const MAX_SPEED = 2;

/** Speed factor clamped to 25–200 % (NaN → 1). */
export function clampSpeed(s: number): number {
  if (!Number.isFinite(s)) return 1;
  return Math.min(MAX_SPEED, Math.max(MIN_SPEED, s));
}

/** A loop clamped to the piece; null when empty or absent. */
export function normalizeLoop(loop: LoopRange | null | undefined, totalTicks: number): LoopRange | null {
  if (!loop || !Number.isFinite(loop.startTick) || !Number.isFinite(loop.endTick)) return null;
  const lo = Math.max(0, Math.min(loop.startTick, loop.endTick, totalTicks));
  const hi = Math.max(0, Math.min(Math.max(loop.startTick, loop.endTick), totalTicks));
  return hi - lo >= 1 ? { startTick: lo, endTick: hi } : null;
}

export interface Anchor {
  /** AudioContext time at which `tick` sounds. */
  time: number;
  tick: number;
  speed: number;
  loop: LoopRange | null;
}

export interface Segment {
  index: number;
  startTime: number;
  /** Infinity when not looping. */
  endTime: number;
  fromTick: number;
  /** Infinity when not looping. */
  toTick: number;
}

/** The loop applies when playback starts before its end. */
export function loopActive(a: Anchor): boolean {
  return !!a.loop && a.tick < a.loop.endTick && a.loop.endTick - a.loop.startTick >= 1;
}

function loopSecs(a: Anchor, map: TimeMap): { first: number; len: number } {
  const loop = a.loop!;
  const b = map.sec(loop.endTick);
  return { first: (b - map.sec(a.tick)) / a.speed, len: (b - map.sec(loop.startTick)) / a.speed };
}

export function segmentOf(a: Anchor, map: TimeMap, index: number): Segment {
  if (!loopActive(a)) return { index: 0, startTime: a.time, endTime: Infinity, fromTick: a.tick, toTick: Infinity };
  const { first, len } = loopSecs(a, map);
  const loop = a.loop!;
  if (index <= 0) return { index: 0, startTime: a.time, endTime: a.time + first, fromTick: a.tick, toTick: loop.endTick };
  const start = a.time + first + (index - 1) * len;
  return { index, startTime: start, endTime: start + len, fromTick: loop.startTick, toTick: loop.endTick };
}

export function segmentIndexAt(a: Anchor, map: TimeMap, time: number): number {
  if (!loopActive(a)) return 0;
  const { first, len } = loopSecs(a, map);
  const t = time - a.time - first;
  if (t < 0) return 0;
  if (len <= 0) return 1;
  return 1 + Math.floor(t / len);
}

/** Context time of a tick within a segment. */
export function timeOfTick(a: Anchor, map: TimeMap, seg: Segment, tick: number): number {
  return seg.startTime + (map.sec(tick) - map.sec(seg.fromTick)) / a.speed;
}

export interface Position {
  segment: number;
  tick: number;
}

/** Played tick sounding at a context time (the anchor tick before the anchor time). */
export function positionAt(a: Anchor, map: TimeMap, time: number): Position {
  const k = segmentIndexAt(a, map, time);
  const seg = segmentOf(a, map, k);
  if (time <= seg.startTime) return { segment: k, tick: seg.fromTick };
  let tick = map.tick(map.sec(seg.fromTick) + (time - seg.startTime) * a.speed);
  if (tick > seg.toTick) tick = seg.toTick;
  if (tick < seg.fromTick) tick = seg.fromTick;
  return { segment: k, tick };
}

export type Cursor = Position;

export function compareCursor(x: Cursor, y: Cursor): number {
  return x.segment - y.segment || x.tick - y.tick;
}

export interface TickRange {
  segment: Segment;
  /** Inclusive. */
  fromTick: number;
  /** Exclusive. */
  toTick: number;
}

/**
 * Tick ranges to schedule to bring the cursor up to `horizon` (a context
 * time). Ranges are contiguous and half-open, so successive calls never
 * schedule an event twice nor skip one.
 */
export function advanceCursor(a: Anchor, map: TimeMap, cursor: Cursor, horizon: number): { ranges: TickRange[]; cursor: Cursor } {
  const target = positionAt(a, map, horizon);
  const ranges: TickRange[] = [];
  let c = cursor;
  if (compareCursor(target, c) <= 0) return { ranges, cursor: c };
  while (c.segment < target.segment) {
    const seg = segmentOf(a, map, c.segment);
    if (c.tick < seg.toTick) ranges.push({ segment: seg, fromTick: c.tick, toTick: seg.toTick });
    const next = segmentOf(a, map, c.segment + 1);
    c = { segment: c.segment + 1, tick: next.fromTick };
  }
  if (target.tick > c.tick) {
    ranges.push({ segment: segmentOf(a, map, c.segment), fromTick: c.tick, toTick: target.tick });
    c = { segment: c.segment, tick: target.tick };
  }
  return { ranges, cursor: c };
}

/** Index of the first item whose key ≥ tick (items sorted by key). */
export function lowerBound<T>(items: readonly T[], tick: number, key: (t: T) => number): number {
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (key(items[mid]) < tick) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export interface Scheduled<T> {
  item: T;
  index: number;
  /** Context time of the onset. */
  time: number;
  segment: Segment;
}

/** Items (sorted by `key`) starting inside a tick range, with their context times. */
export function itemsInRange<T>(items: readonly T[], key: (t: T) => number, range: TickRange, a: Anchor, map: TimeMap): Scheduled<T>[] {
  const out: Scheduled<T>[] = [];
  for (let i = lowerBound(items, range.fromTick, key); i < items.length; i++) {
    const k = key(items[i]);
    if (k >= range.toTick) break;
    out.push({ item: items[i], index: i, time: timeOfTick(a, map, range.segment, k), segment: range.segment });
  }
  return out;
}

/** Note-off time of an event (cut at the loop end inside a loop). */
export function noteEndTime(a: Anchor, map: TimeMap, seg: Segment, ev: Pick<NoteEvent, 'startTick' | 'durTicks'>): number {
  let end = ev.startTick + Math.max(1, ev.durTicks);
  if (end > seg.toTick) end = seg.toTick;
  return timeOfTick(a, map, seg, end);
}

/**
 * Where playback starts: a requested tick clamped to the piece; with a loop,
 * a start outside the loop jumps to the loop start; at/after the end, the
 * start of the piece.
 */
export function startTickFor(requested: number, totalTicks: number, loop: LoopRange | null): number {
  let t = Number.isFinite(requested) ? Math.max(0, requested) : 0;
  if (loop && (t < loop.startTick || t >= loop.endTick)) t = loop.startTick;
  if (t >= totalTicks) t = 0;
  return t;
}

/** Tempo (quarter notes per minute) at a tick. */
export function qpmAt(tl: Pick<Timeline, 'tempo'>, tick: number): number {
  const pts = tl.tempo;
  if (!pts.length) return 120;
  const i = lowerBound(pts, tick + 1e-9, (p) => p.tick) - 1;
  return pts[Math.max(0, i)].qpm;
}

export interface CountInClick {
  /** Seconds after the count-in starts. */
  offset: number;
  accent: boolean;
  /** 0-based beat number across the whole count-in. */
  beat: number;
  /** 0-based count-in bar. */
  bar: number;
}

export interface CountInPlan {
  clicks: CountInClick[];
  /** Seconds from the first click to the downbeat of the start tick. */
  duration: number;
}

/** Count-in of N bars at the tempo of the start position (speed applied). */
export function countInPlan(opts: { bars: number; barTicks: number; groups: readonly number[]; qpm: number; speed: number }): CountInPlan {
  const bars = Math.max(0, Math.floor(opts.bars));
  const secPerTick = 60 / (opts.qpm * PPQ) / clampSpeed(opts.speed);
  const groups = opts.groups.length ? opts.groups : [opts.barTicks];
  const clicks: CountInClick[] = [];
  let beat = 0;
  for (let b = 0; b < bars; b++) {
    let acc = 0;
    groups.forEach((g, j) => {
      clicks.push({ offset: (b * opts.barTicks + acc) * secPerTick, accent: j === 0, beat: beat++, bar: b });
      acc += g;
    });
  }
  return { clicks, duration: bars * opts.barTicks * secPerTick };
}

/** Which tracks are heard: solo wins over mute. */
export function audibleTracks(tracks: readonly Pick<Track, 'mute' | 'solo'>[]): boolean[] {
  const anySolo = tracks.some((t) => t.solo);
  return tracks.map((t) => (anySolo ? !!t.solo : !t.mute));
}

/** Default track volume in the model; it maps to unity gain. */
export const DEFAULT_TRACK_VOLUME = 0.8;

/** Linear gain of a track volume (0…1, quadratic taper, 0.8 = unity). */
export function volumeGain(volume: number | undefined): number {
  const v = Math.max(0, Math.min(1, volume ?? DEFAULT_TRACK_VOLUME));
  return (v / DEFAULT_TRACK_VOLUME) ** 2;
}

/**
 * Events played legato (target of a hammer-on/pull-off/legato slide): the
 * next event on the same track/voice/string after an event flagged `legato`.
 */
export function legatoTargets(events: readonly NoteEvent[]): Uint8Array {
  const out = new Uint8Array(events.length);
  const pending = new Map<string, NoteEvent>();
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const key = `${e.track}:${e.voice}:${e.string ?? '-'}`;
    const src = pending.get(key);
    if (src && e.startTick > src.startTick && e.startTick <= src.startTick + src.durTicks + PPQ / 8) {
      out[i] = 1;
      pending.delete(key);
    }
    if (e.legato) pending.set(key, e);
  }
  return out;
}

/** Sounding MIDI to synthesize for an event (harmonics sound their partial). */
export function eventMidi(e: Pick<NoteEvent, 'midi' | 'harmonic' | 'fret'>): number {
  return e.harmonic ? harmonicMidi(e.midi, e.harmonic, e.fret) : e.midi;
}
